// MyCertificate.jsx
// PUBLIC page — URL: /my-certificate
// Student enters their email -> approved certificate is shown -> download as PDF / PNG

import { useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const VIEW = {
  FORM:    "form",
  LOADING: "loading",
  FOUND:   "found",
  MESSAGE: "message", // pending / rejected / not-found / error
};

export default function MyCertificate() {
  const [view, setView]   = useState(VIEW.FORM);
  const [email, setEmail] = useState("");
  const [certs, setCerts] = useState([]);
  const [msg, setMsg]     = useState({ title: "", text: "", tone: "error" });

  async function handleSubmit(e) {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) return;

    setView(VIEW.LOADING);

    try {
      const res = await fetch(`${API_BASE}/my-certificate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      });

      const data = await res.json();

      if (data.success) {
        setCerts(data.certificates || []);
        setView(VIEW.FOUND);
        return;
      }

      const tones = { PENDING: "warn", REJECTED: "error", DB_DOWN: "warn" };
      const titles = {
        PENDING:    "⏳ Approval Pending",
        REJECTED:   "❌ Request Not Approved",
        NOT_FOUND:  "🔍 No Certificate Found",
        DB_DOWN:    "⚠️ Service Temporarily Unavailable",
        RATE_LIMIT: "🚫 Too Many Attempts",
        BAD_EMAIL:  "✉️ Invalid Email",
      };

      setMsg({
        title: titles[data.code] || "Something Went Wrong",
        text:  data.reason ? `${data.message}\n\nReason: ${data.reason}` : data.message,
        tone:  tones[data.code] || "error",
      });
      setView(VIEW.MESSAGE);
    } catch {
      setMsg({
        title: "⚠️ Connection Problem",
        text: "We could not reach the server. Please check your internet connection and try again.",
        tone: "warn",
      });
      setView(VIEW.MESSAGE);
    }
  }

  function reset() {
    setView(VIEW.FORM);
    setCerts([]);
  }

  function openCertificate(c) {
    const q = new URLSearchParams({
      name:           c.name,
      course:         c.course,
      months:         c.months || "",
      completionDate: c.completionDate || "",
      verifyId:       c.verifyId || "",
    });
    window.location.href = `/certificate?${q.toString()}`;
  }

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={S.logo}>🎓 Uncodemy</div>
        <p style={S.tagline}>Certificate Download Portal</p>
      </header>

      <main style={S.card}>

        {/* ── Email form ───────────────────────────── */}
        {(view === VIEW.FORM || view === VIEW.LOADING) && (
          <form onSubmit={handleSubmit}>
            <h1 style={S.h1}>Download Your Certificate</h1>
            <p style={S.lead}>
              Enter the same email address you used when requesting your
              certificate. If it has been approved, it will appear right away.
            </p>

            <label style={S.label} htmlFor="email">Email Address</label>
            <input
              id="email"
              style={S.input}
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={view === VIEW.LOADING}
              autoComplete="email"
              required
              autoFocus
            />

            <button style={S.btnPrimary} type="submit" disabled={view === VIEW.LOADING}>
              {view === VIEW.LOADING ? "🔎 Searching..." : "Find My Certificate →"}
            </button>

            <p style={S.hint}>
              First time here? Please{" "}
              <a href="/request" style={S.link}>submit a certificate request</a> instead.
            </p>
          </form>
        )}

        {/* ── Certificate found ────────────────────── */}
        {view === VIEW.FOUND && (
          <div>
            <div style={S.successBadge}>
              ✅ {certs.length} Certificate{certs.length > 1 ? "s" : ""} Found
            </div>
            <h1 style={S.h1}>Your Certificate Is Ready</h1>

            {certs.map((c) => (
              <div key={c.id} style={S.certCard}>
                <div style={S.certCourse}>{c.course || "—"}</div>
                <div style={S.certName}>{c.name}</div>

                <dl style={S.dl}>
                  <Row label="Duration"        value={c.months ? `${c.months} Months` : "—"} />
                  <Row label="Completion Date" value={fmtDate(c.completionDate)} />
                  <Row label="Issued On"       value={fmtIssued(c.completionDate, c.approvedAt)} />
                  <Row label="Verification ID" value={c.verifyId} mono />
                </dl>

                <button style={S.btnPrimary} onClick={() => openCertificate(c)}>
                  📥 Open &amp; Download (PDF / PNG)
                </button>

                <a
                  href={`/verify/${c.verifyId}`}
                  target="_blank"
                  rel="noreferrer"
                  style={S.btnGhost}
                >
                  🔗 Open Verification Page
                </a>

                {c.downloadCount > 0 && (
                  <p style={S.metaLine}>
                    Downloaded {c.downloadCount} time{c.downloadCount > 1 ? "s" : ""} before.
                    There is no limit on re-downloads.
                  </p>
                )}
              </div>
            ))}

            <button style={S.btnGhostSm} onClick={reset}>← Try a different email</button>
          </div>
        )}

        {/* ── Message states ───────────────────────── */}
        {view === VIEW.MESSAGE && (
          <div style={{ textAlign: "center" }}>
            <h1 style={{ ...S.h1, textAlign: "center" }}>{msg.title}</h1>
            <div style={msg.tone === "warn" ? S.warnBox : S.errorBox}>{msg.text}</div>
            <button style={S.btnPrimary} onClick={reset}>← Try Again</button>
            <p style={S.hint}>
              First time here?{" "}
              <a href="/request" style={S.link}>Submit a new request</a>
              <br />
              Need help?{" "}
              <a href="mailto:admin@uncodemy.com" style={S.link}>admin@uncodemy.com</a>
            </p>
          </div>
        )}
      </main>

      <footer style={S.footer}>© Uncodemy Edutech Pvt. Ltd.</footer>
    </div>
  );
}

/* ── helpers ───────────────────────────────────────── */

function Row({ label, value, mono }) {
  return (
    <div style={S.row}>
      <dt style={S.dt}>{label}</dt>
      <dd style={{ ...S.dd, ...(mono ? S.monoValue : {}) }}>{value || "—"}</dd>
    </div>
  );
}

// Handles dd-mm-yyyy, yyyy-mm-dd and ISO strings
function parseDate(str) {
  if (!str) return null;
  const s = String(str).trim();
  const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(str) {
  const d = parseDate(str);
  return d
    ? d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : str || "—";
}

// completion date + 10 days (same rule as the verification page)
function fmtIssued(completionDate, approvedAt) {
  const d = parseDate(completionDate);
  if (d) {
    d.setDate(d.getDate() + 10);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }
  return fmtDate(approvedAt);
}

/* ── styles ────────────────────────────────────────── */

const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f0f4ff 0%, #fff5f0 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: { textAlign: "center", marginBottom: 28 },
  logo: { fontSize: 28, fontWeight: 700, color: "#ff5421" },
  tagline: { color: "#64748b", fontSize: 14, marginTop: 4 },
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 28px rgba(30,27,75,0.10)",
    padding: "34px 36px",
    width: "100%",
    maxWidth: 520,
    boxSizing: "border-box",
  },
  h1: { fontSize: 22, fontWeight: 700, color: "#1e1b4b", margin: "0 0 10px" },
  lead: { color: "#64748b", fontSize: 14, lineHeight: 1.65, margin: "0 0 22px" },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
  input: {
    width: "100%",
    padding: "13px 15px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    fontSize: 15,
    marginBottom: 18,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
  },
  btnPrimary: {
    width: "100%",
    padding: "14px",
    background: "#ff5421",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnGhost: {
    display: "block",
    width: "100%",
    padding: "12px",
    marginTop: 10,
    background: "#fff",
    color: "#ff5421",
    border: "1.5px solid #ffd7c8",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "center",
    textDecoration: "none",
    boxSizing: "border-box",
  },
  btnGhostSm: {
    width: "100%",
    padding: "11px",
    marginTop: 18,
    background: "#f8fafc",
    color: "#475569",
    border: "none",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  hint: { color: "#94a3b8", fontSize: 13, textAlign: "center", marginTop: 16, lineHeight: 1.9 },
  link: { color: "#ff5421", fontWeight: 600 },
  successBadge: {
    background: "#d1fae5",
    color: "#065f46",
    fontSize: 13,
    fontWeight: 600,
    padding: "6px 14px",
    borderRadius: 20,
    display: "inline-block",
    marginBottom: 14,
  },
  certCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "20px 22px",
    marginBottom: 16,
  },
  certCourse: {
    fontSize: 12, fontWeight: 700, color: "#ff5421",
    letterSpacing: "0.06em", textTransform: "uppercase",
  },
  certName: { fontSize: 20, fontWeight: 700, color: "#1e1b4b", margin: "6px 0 16px" },
  dl: { margin: "0 0 18px" },
  row: {
    display: "flex", justifyContent: "space-between", gap: 16,
    padding: "8px 0", borderBottom: "1px solid #eef2f7",
  },
  dt: { fontSize: 13, color: "#64748b", fontWeight: 500, margin: 0 },
  dd: { fontSize: 14, color: "#1e1b4b", fontWeight: 600, margin: 0, textAlign: "right" },
  monoValue: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 13, color: "#15803d",
  },
  metaLine: { fontSize: 12, color: "#94a3b8", marginTop: 12, marginBottom: 0, lineHeight: 1.6 },
  warnBox: {
    background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e",
    borderRadius: 10, padding: "14px 18px", fontSize: 14, lineHeight: 1.7,
    marginBottom: 20, whiteSpace: "pre-line", textAlign: "left",
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    borderRadius: 10, padding: "14px 18px", fontSize: 14, lineHeight: 1.7,
    marginBottom: 20, whiteSpace: "pre-line", textAlign: "left",
  },
  footer: { color: "#94a3b8", fontSize: 12, marginTop: 26, textAlign: "center" },
};